'use client'

import { cx } from 'classix'
import {
	type Cta,
	HeroBannerShopType,
	type PointOfInterestImage,
	type Video,
} from 'landings/Home/Home.type'
import dynamic from 'next/dynamic'
import { useState } from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import { SimpleVideo } from 'videos/components/SimpleVideo/SimpleVideo'
import animationStyles from '../../../../MotionAnimations.module.css'
import focusStyle from '../../../../Shared/A11y/TabFocus.module.css'
import { ButtonCtas } from '../../../../Shared/ctas/ButtonCtas/ButtonCtas'
import { HeroBannerImage } from '../../../components/HeroBannerImage/HeroBannerImage'
import styles from './HeroBannerShopSalesContent.module.css'

const HeroBannerShopSalesText = dynamic(() =>
	import('../HeroBannerShopSalesText/HeroBannerShopSalesText').then(
		(mod) => mod.HeroBannerShopSalesText
	)
)

interface HeroBannerShopSalesContentProps {
	readonly isCicd: boolean
	readonly isPrioritySlot: boolean | undefined
	readonly type: HeroBannerShopType
	readonly video: {
		videoPortrait?: Video
		videoLandscape?: Video
		videoPlaceholder?: PointOfInterestImage | null
	} | null
	readonly videoButtonTitle: {
		play: string
		pause: string
	}
	readonly titleKey: string
	readonly inverse: boolean
	readonly ctas: Cta[]
	readonly subtitleTopKey?: string
	readonly discount?: string
	readonly subtitleBottomGroup?: {
		titleKey: string
		subtitleKey: string
	}[]
	readonly subtitleBottomKey?: string
}

export const HeroBannerShopSalesContent = ({
	isCicd,
	isPrioritySlot,
	type,
	video,
	videoButtonTitle,
	subtitleTopKey,
	titleKey,
	discount,
	subtitleBottomGroup,
	subtitleBottomKey,
	inverse,
	ctas,
}: HeroBannerShopSalesContentProps) => {
	const isVideo = type === HeroBannerShopType.video
	const { isLargeOrGreater } = useResponsive()
	const [canRenderText, setCanRenderText] = useState(!isVideo)

	const handleVideoCanPlay = () => {
		setCanRenderText(true)
	}

	return (
		<>
			{isVideo && video ? (
				<SimpleVideo
					disableAutoplay={isCicd}
					video={video}
					inverse={inverse}
					videoButtonTitle={videoButtonTitle}
					videoButtonClassName={cx(styles.videoButton, styles.opacityFadeIn)}
					onVideoCanPlay={handleVideoCanPlay}
					contentIfVideoCantPlay={
						<HeroBannerImage
							image={video.videoPlaceholder}
							priority={isPrioritySlot}
						/>
					}
				/>
			) : null}
			{canRenderText ? (
				<div
					className={cx(styles.contentWrapper, animationStyles.opacityFadeIn)}
				>
					<HeroBannerShopSalesText
						subtitleTopKey={subtitleTopKey}
						titleKey={titleKey}
						discount={discount}
						subtitleBottomGroup={subtitleBottomGroup}
						subtitleBottomKey={subtitleBottomKey}
						inverse={inverse}
						isLargeOrGreater={isLargeOrGreater}
					/>
					<ButtonCtas
						ctas={ctas}
						className={styles.ctaWrapper}
						buttonClassName={cx(styles.buttonCta, focusStyle.bannerCtaFocus)}
						data-testid='landings.home.heroBannerShopSalesLink'
						listenTextOverflow
					/>
				</div>
			) : null}
		</>
	)
}
